import { Loader2 } from "lucide-react"
import type React from "react"
import { useEffect, useMemo, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { cn } from "../../../lib/utils"
import { Button } from "../../ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card"
import AnimatedBackground from "../../utils/AnimatedBackground"
import Footer from "../../utils/Footer"
import TopBar from "../../utils/TopBar"
import { useEditCashflow } from "../hooks/EditCashflow"
import { CashflowHistoryItem } from "../models/ModelCashflow"

const StyledInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = ({ className, value, onChange }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value?.toString() || '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(value?.toString() || '');
    }
  }, [value, isEditing]);

  if (!isEditing) {
    return (
      <div
        onClick={() => setIsEditing(true)}
        className={cn(
          "cursor-pointer text-blue-400",
          className
        )}
      >
        {formatCurrency(Number(value), false)}
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/[^0-9.-]/g, '');
    
    // Ensure only one decimal point
    const parts = newValue.split('.');
    const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : newValue;
    
    setLocalValue(validValue);
    if (onChange) {
      onChange({ target: { value: validValue } } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      value={localValue}
      onChange={handleChange}
      onBlur={() => setIsEditing(false)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          setIsEditing(false);
        }
      }}
      autoFocus
      className={cn(
        "w-full text-right bg-gray-800 text-blue-400 px-3 py-2 focus:outline-none",
        className
      )}
    />
  );
};

const formatCurrency = (value: number, showDecimals = false) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  }).format(value)
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("default", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC"
  });
};

const EditCashflow: React.FC = () => {
  const { cashflowHistory, fetchCashflowData, updateCashflowData, handleFieldUpdate, isLoading, isSaving, error } =
    useEditCashflow()

  // Calculate derived values for each month
  const calculatedCashflow = useMemo(() => {
    return cashflowHistory.map((item) => {
      // Base Cashflow calculation
      const baseCashflow = item.income - item.expenses + (Number(item.others) || 0);
      
      // VR calculations
      const vrGains = Number(item.gains_vr) || 0;
      const vrExpenses = Number(item.expenses_vr) || 0;
      const finalVR = (Number(item.initial_vr) || 0) + vrGains - vrExpenses;
      
      // Investment calculations
      const projectedInvestments = Number(item.projected_investments) || 0;
      const investmentsBuy = Math.max(projectedInvestments, 0);
      const investmentsSell = Math.abs(Math.min(projectedInvestments, 0));
      const finalInvestments = (Number(item.made_investments) || 0) + investmentsBuy - investmentsSell;
      
      // Cash Reserve calculations
      const projectedCashReserve = Number(item.projected_cash_reserve) || 0;
      const cashReserveAdd = Math.max(projectedCashReserve, 0);
      const cashReserveRemove = Math.abs(Math.min(projectedCashReserve, 0));
      const finalCashReserve = (Number(item.initial_cash_reserve) || 0) + cashReserveAdd - cashReserveRemove;
      
      // Final Cash calculation
      const initialCash = Number(item.initial_cash) || 0;
      const finalCash = initialCash + baseCashflow - projectedInvestments - projectedCashReserve;

      return {
        ...item,
        base_cashflow: baseCashflow,
        final_vr: finalVR,
        final_investments: finalInvestments,
        final_cash_reserve: finalCashReserve,
        final_cash: finalCash
      };
    });
  }, [cashflowHistory]);

  useEffect(() => {
    fetchCashflowData()
  }, [fetchCashflowData])

  const handleSave = async () => {
    const success = await updateCashflowData(calculatedCashflow)
    if (success) {
      // Optionally show success message
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex items-center gap-2 text-blue-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading cashflow data...</span>
        </div>
      </div>
    )
  }

  if (error === "income") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">No Income Data Available</h2>
          <p className="text-gray-400 mb-6">Please add some income data to view your cashflow.</p>
          <Link to="/income">
            <Button className="bg-green-600 hover:bg-green-500 text-white">Add Income Data</Button>
          </Link>
        </div>
      </div>
    )
  }

  if (error === "expense") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4 text-gray-200">No Expense Data Available</h2>
          <p className="text-gray-400 mb-6">Please add some expense data to view your cashflow.</p>
          <Link to="/expenses">
            <Button className="bg-red-600 hover:bg-red-500 text-white">Add Expense Data</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar title="Edit Cashflow" subtitle="Edit your cashflow data" isLoading={isLoading || isSaving} />

        <main className="flex-grow container mx-auto py-2 px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6">
            <div className="h-full">
              <Card className="bg-black border-gray-800 h-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Monthly Cashflow</CardTitle>
                    <CardDescription className="text-gray-300">Edit your cashflow data</CardDescription>
                  </div>
                  <div className="flex gap-4">
                    <Button
                      onClick={handleSave}
                      className="bg-green-600 hover:bg-green-500 text-white font-semibold px-6 py-2 rounded-full"
                      disabled={isLoading || isSaving}
                    >
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Save Changes
                    </Button>
                    <Link to="/cashflow">
                      <Button className="bg-gray-600 hover:bg-gray-500 text-white font-semibold px-6 py-2 rounded-full">
                        Cancel
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr>
                          <th className="text-left text-gray-300 py-3 px-4"></th>
                          {calculatedCashflow.map((item) => (
                            <th key={item.month} className="text-right text-gray-300 py-3 px-4">
                              {formatDate(item.month)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Income Row - Read Only */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+) Income</td>
                          {calculatedCashflow.map((item, _index) => (
                            <td key={item.month} className="py-3 px-4 text-right text-green-400">
                              {formatCurrency(item.income)}
                            </td>
                          ))}
                        </tr>

                        {/* Expenses Row - Read Only */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(-) Expenses</td>
                          {calculatedCashflow.map((item, _index) => (
                            <td key={item.month} className="py-3 px-4 text-right text-red-400">
                              {formatCurrency(item.expenses)}
                            </td>
                          ))}
                        </tr>

                        {/* Additional Cashflow - Editable */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Others</td>
                          {calculatedCashflow.map((item, index) => {
                            const additionalCashflow = Number(item.others) || 0;
                            return (
                              <td key={item.month} className="py-3 px-4 text-right">
                                <StyledInput
                                  type="number"
                                  value={additionalCashflow}
                                  onChange={(e) =>
                                    handleFieldUpdate(index, 'others' as keyof CashflowHistoryItem, Number(e.target.value))
                                  }
                                  className="text-blue-400"
                                />
                              </td>
                            );
                          })}
                        </tr>

                        {/* Base Cashflow - Calculated */}
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">(=) Base Cashflow</td>
                          {calculatedCashflow.map((item) => (
                            <td
                              key={item.month}
                              className={`py-3 px-4 text-right ${item.base_cashflow >= 0 ? "text-green-400" : "text-red-400"}`}
                            >
                              {formatCurrency(item.base_cashflow)}
                            </td>
                          ))}
                        </tr>

                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={calculatedCashflow.length + 1} className="py-2"></td>
                        </tr>

                        {/* VR Section - Editable */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial VR</td>
                          {calculatedCashflow.map((item, index) => (
                            <td key={item.month} className="py-3 px-4 text-right">
                              <StyledInput
                                type="number"
                                value={item.initial_vr}
                                onChange={(e) => handleFieldUpdate(index, "initial_vr", Number(e.target.value))}
                                className="text-blue-400"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+) Gains</td>
                          {calculatedCashflow.map((item, _index) => (
                            <td key={item.month} className="py-3 px-4 text-right text-green-400">
                              {formatCurrency(item.gains_vr)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(-) Expenses</td>
                          {calculatedCashflow.map((item, index) => (
                            <td key={item.month} className="py-3 px-4 text-right">
                              <StyledInput
                                type="number"
                                value={item.expenses_vr}
                                onChange={(e) => handleFieldUpdate(index, "expenses_vr", Number(e.target.value))}
                                className="text-blue-400"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">(=) Final VR</td>
                          {calculatedCashflow.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_vr)}
                            </td>
                          ))}
                        </tr>

                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={calculatedCashflow.length + 1} className="py-2"></td>
                        </tr>

                        {/* Investment Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial Investments</td>
                          {calculatedCashflow.map((item, index) => (
                            <td key={item.month} className="py-3 px-4 text-right">
                              <StyledInput
                                type="number"
                                value={item.made_investments}
                                onChange={(e) => handleFieldUpdate(index, "made_investments", Number(e.target.value))}
                                className="text-blue-400"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Investments</td>
                          {calculatedCashflow.map((item, index) => (
                            <td key={item.month} className="py-3 px-4 text-right">
                              <StyledInput
                                type="number"
                                value={item.projected_investments}
                                onChange={(e) => handleFieldUpdate(index, "projected_investments", Number(e.target.value))}
                                className="text-blue-400"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">(=) Final Investments</td>
                          {calculatedCashflow.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_investments)}
                            </td>
                          ))}
                        </tr>

                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={calculatedCashflow.length + 1} className="py-2"></td>
                        </tr>

                        {/* Cash Reserve Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial Cash Reserve</td>
                          {calculatedCashflow.map((item, index) => (
                            <td key={item.month} className="py-3 px-4 text-right">
                              <StyledInput
                                type="number"
                                value={item.initial_cash_reserve}
                                onChange={(e) => handleFieldUpdate(index, "initial_cash_reserve", Number(e.target.value))}
                                className="text-blue-400"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Cash Reserve</td>
                          {calculatedCashflow.map((item, index) => (
                            <td key={item.month} className="py-3 px-4 text-right">
                              <StyledInput
                                type="number"
                                value={item.projected_cash_reserve}
                                onChange={(e) => handleFieldUpdate(index, "projected_cash_reserve", Number(e.target.value))}
                                className="text-blue-400"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">(=) Final Cash Reserve</td>
                          {calculatedCashflow.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_cash_reserve)}
                            </td>
                          ))}
                        </tr>

                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={calculatedCashflow.length + 1} className="py-2"></td>
                        </tr>

                        {/* Final Cash Section */}
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">Initial Cash</td>
                          {calculatedCashflow.map((item, index) => (
                            <td key={item.month} className="py-3 px-4 text-right">
                              <StyledInput
                                type="number"
                                value={item.initial_cash}
                                onChange={(e) => handleFieldUpdate(index, "initial_cash", Number(e.target.value))}
                                className="text-blue-400"
                              />
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Base Cashflow</td>
                          {calculatedCashflow.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.base_cashflow)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Net Investments</td>
                          {calculatedCashflow.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(-item.projected_investments)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800">
                          <td className="py-3 px-8 text-gray-300">(+/-) Net Cash Reserve</td>
                          {calculatedCashflow.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(-item.projected_cash_reserve)}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-t border-gray-800 font-semibold">
                          <td className="py-3 px-4 text-gray-300">Final Cash</td>
                          {calculatedCashflow.map((item) => (
                            <td key={item.month} className="py-3 px-4 text-right text-gray-300">
                              {formatCurrency(item.final_cash)}
                            </td>
                          ))}
                        </tr>

                        <tr className="border-t border-gray-800 bg-gray-900/30">
                          <td colSpan={calculatedCashflow.length + 1} className="py-2"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  )
}

export default EditCashflow

